import AddUserToTeamOrOrgModal from 'components/add-user-to-team-or-org-modal'
import useOrganization from 'hooks/use-organization'
import useUpdateOrganization from 'hooks/use-update-organization'
import useRecentOrganizations from 'hooks/use-recent-organizations'
import { Organization } from 'interfaces/organization'
import React, { useEffect, useState } from 'react'
import {
  Breadcrumb,
  Button,
  Col,
  Container,
  FormControl,
  Row,
} from 'react-bootstrap'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import OrganizationDetailsCard from 'components/organization-details-page/organization-details-card'
import HubSpotButton from 'components/hubspot-button'
import { HubspotTicketsModal } from 'components/organization-details-page/hubspot-tickets-modal'
import { UpdateOrgSubscriptionIdModal } from 'components/organization-details-page/update-org-subscription-id-modal'
import { OverrideSubscriptionStatusModal } from 'components/organization-details-page/override-subscription-status-modal'
import { OrganizationStripeCard } from 'components/organization-details-page/organization-stripe-card'
import { OrganizationAllSettingsCard } from 'components/organization-details-page/organization-all-settings-card'
import OrganizationMembersTable from 'components/organization-details-page/organization-members-table'
import OrganizationHubspotNotes from 'components/organization-details-page/organization-hubspot-note'
import { useGetOrganizationUsersWithAppleIds } from 'hooks/use-get-organization-users-with-apple-ids'
import useOrganizationPhoneNumbers from 'hooks/use-organization-phone-numbers'
import { EditOrgFieldsModal } from 'components/organization-details-page/edit-org-fields-modal'
import OrganizationPhoneNumbersTable from 'components/organization-details-page/organization-phone-numbers-table'
import { useGetInternalAdmins } from 'hooks/use-get_internal-admins'
import { PhoneNumber } from 'interfaces/phone-number'
import { OrganizationPartnerTokenCard } from 'components/organization-details-page/organization-partner-token-card'
import OrganizationIntegrationsCard from 'components/user-details-page/organization-integrations-card'
import OrganizationAssociatedOrganizationsTable from 'components/organization-details-page/associated-organizations-table'
import useOrganizationAssociatedOrganizations from 'hooks/use-organization-associated-organizations'
import hubspotLogo from 'images/hubspot-logo.svg'
const OrganizationDetailPage = () => {
  const params = useParams<{ id: string }>()
  const { id: orgId } = params

  const [showAddUserToOrganizationModal, setShowAddUserToOrganizationModal] =
    useState(false)
  const [showSubscriptionIdUpdateModal, setShowSubscriptionIdUpdateModal] =
    useState(false)

  const [
    showUpdateSubscriptionStatusOverrideModal,
    setShowUpdateSubscriptionStatusOverrideModal,
  ] = useState(false)

  const [showUpdateSaleRepModal, setShowUpdateSaleRepModal] = useState(false)

  const [showEditOrgField, setShowEditOrgField] = useState(false)
  const [orgFieldModalParams, setOrgFieldModalParams] = useState<
    React.ComponentProps<typeof FormControl>
  >({
    value: '',
  })

  const [editableOrg, setEditableOrg] = useState<Partial<Organization>>()
  const [showHubspotTicketsModal, setShowHubspotTicketsModal] = useState(false)

  const {
    data: org,
    isFetching,
    isSuccess: isOrgLoaded,
  } = useOrganization(orgId)
  const { data: internalAdmins, isLoading: isInternalAdminsLoading } =
    useGetInternalAdmins()

  // Batch 2: Wait for org success, then load users with 500ms delay
  const { data: usersWithAppleIdsData, isSuccess: isUsersLoaded } =
    useGetOrganizationUsersWithAppleIds(orgId, isOrgLoaded)

  // Batch 3: Wait for users success, then load phone numbers
  const { data: phoneNumbers, isFetching: isPhoneNumbersFetching } =
    useOrganizationPhoneNumbers(orgId, isUsersLoaded)

  // Fetch associated organizations
  const {
    data: associatedOrganizationsData,
    isLoading: isAssociatedOrgsLoading,
  } = useOrganizationAssociatedOrganizations(orgId)

  const { mutate: updateOrganization, isLoading: isUpdatingOrg } =
    useUpdateOrganization(orgId)

  // Recent organizations tracking
  const { addRecentOrganization } = useRecentOrganizations()

  const handleAddUserToOrganizationClicked = () => {
    setShowAddUserToOrganizationModal(true)
  }

  type Field = {
    fieldKey: string
    fieldLabel: string
    ariaId: string
    type: string
  }

  useEffect(() => {
    if (!org) return
    setEditableOrg(org)
  }, [org])

  // Track organization when page loads and org data is available
  useEffect(() => {
    if (org && org.id && org.name) {
      addRecentOrganization({
        id: org.id,
        name: org.name,
      })
    }
  }, [org?.id, org?.name, addRecentOrganization])

  const handleEditFieldClick = ({
    fieldKey,
    fieldLabel,
    ariaId,
    type,
  }: Field) => {
    setOrgFieldModalParams({
      title: fieldLabel,
      fieldKey,
      type,
      placeholder: fieldLabel,
      ariaLabel: fieldLabel,
      ariaDescribedBy: ariaId,
      buttonId: ariaId,
    })
    setShowEditOrgField(true)
  }

  const handleEditHubspotUrlClicked = () => {
    handleEditFieldClick({
      fieldKey: 'internal_crm_url',
      fieldLabel: 'Hubspot Url',
      ariaId: 'crm-url-button',
      type: 'url',
    })
  }

  const handleEditTierClicked = () => {
    handleEditFieldClick({
      fieldKey: 'organization_internal_metadatum_attributes.tier',
      fieldLabel: 'Tier',
      ariaId: 'tier-picker',
      type: 'string',
    })
  }

  const handleEditNameClicked = () => {
    handleEditFieldClick({
      fieldKey: 'name',
      fieldLabel: 'Organization Name',
      ariaId: 'org-name-edit',
      type: 'string',
    })
  }

  const resetTier = () => {
    handleSaveFieldClick(
      {
        id: editableOrg?.id,
        organization_internal_metadatum_attributes: {
          tier: null,
        },
      },
      'Tier'
    )
  }

  const handleSaveFieldClick = (
    newOrg: Partial<Organization> = {},
    fieldName: string
  ) => {
    updateOrganization(
      { organization: newOrg },
      {
        onSuccess: () => {
          setShowEditOrgField(false)
          toast(`${fieldName} Updated!`, { icon: `👍` })
        },
      }
    )
  }

  const handleEditOrgFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableOrg((tempEditableOrg) => {
      const keyParts = orgFieldModalParams.fieldKey.split('.')
      const org: Record<string, any> = tempEditableOrg || {}
      if (keyParts.length === 1) {
        org[keyParts[0]] = e.target.value
        return org
      }

      let cur = org
      if (keyParts.length > 1) {
        for (let i = 0; i < keyParts.length - 1; i++) {
          cur[keyParts[i]] = cur[keyParts[i]] || {}
          cur = cur[keyParts[i]]
        }
      }

      cur[keyParts[keyParts.length - 1]] = e.target.value
      return org
    })
  }

  const isInHouseTrial =
    org?.organization_subscription?.subscription_status === 'trialing' &&
    org?.organization_subscription?.trial_end &&
    org?.organization_subscription?.subscription_id?.includes('trial')

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/organizations">Organizations</Breadcrumb.Item>
        <Breadcrumb.Item active>
          {org?.name || 'Organization Details'}
        </Breadcrumb.Item>
      </Breadcrumb>
      {(org?.internal_crm_url || org?.hubspot_company_url) && (
        <div
          style={{
            marginTop: '8px',
            marginBottom: '8px',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
          }}
        >
          {org?.internal_crm_url && (
            <HubSpotButton url={org.internal_crm_url} />
          )}
          {org?.hubspot_company_url && (
            <HubSpotButton url={org.hubspot_company_url} />
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowHubspotTicketsModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <img
              src={hubspotLogo}
              alt="HubSpot"
              style={{ width: '16px', height: '16px' }}
            />
            View Hubspot Tickets
          </Button>
          <Button
            variant="secondary"
            size="sm"
            href={`/organizations/${orgId}/blocked_numbers`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Manage Blocked Numbers
          </Button>
        </div>
      )}
      {isFetching ? (
        <div className="m-2">Loading...</div>
      ) : (
        <>
          <Row className="mb-4">
            <Col lg="6">
              <OrganizationDetailsCard
                org={org}
                setShowUpdateSaleRepModal={setShowUpdateSaleRepModal}
                handleEditTierClicked={handleEditTierClicked}
                resetTier={resetTier}
                setShowUpdateSubscriptionStatusOverrideModal={
                  setShowUpdateSubscriptionStatusOverrideModal
                }
                handleEditHubspotUrlClicked={handleEditHubspotUrlClicked}
                handleEditNameClicked={handleEditNameClicked}
                showUpdateSaleRepModal={showUpdateSaleRepModal}
                orgId={orgId}
                isInHouseTrial={!!isInHouseTrial}
                internalAdmins={internalAdmins}
                isInternalAdminsLoading={isInternalAdminsLoading}
              />
              <OrganizationPartnerTokenCard orgId={orgId} />
            </Col>

            <Col lg="6">
              <OrganizationStripeCard
                org={org}
                setShowSubscriptionIdUpdateModal={
                  setShowSubscriptionIdUpdateModal
                }
                internalAdmins={internalAdmins}
                orgId={orgId}
                isInHouseTrial={!!isInHouseTrial}
              />
              <OrganizationIntegrationsCard
                orgId={orgId}
                organizationName={org?.name || 'Organization'}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <OrganizationMembersTable
                handleAddUserToOrganizationClicked={
                  handleAddUserToOrganizationClicked
                }
                orgId={orgId}
                usersWithAppleIdsData={usersWithAppleIdsData}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <OrganizationPhoneNumbersTable
                orgId={orgId}
                usersWithAppleIdsData={usersWithAppleIdsData}
                phoneNumbers={phoneNumbers}
                isPhoneNumbersFetching={isPhoneNumbersFetching}
                redactMessageHistory={org?.redact_message_history}
                customerProductType={
                  org?.organization_subscription?.customer_product_type
                }
              />
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <OrganizationAssociatedOrganizationsTable
                orgId={orgId}
                associatedOrganizations={
                  associatedOrganizationsData?.organizations
                }
                isLoading={isAssociatedOrgsLoading}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <OrganizationAllSettingsCard
                org={org}
                orgId={orgId}
                handleSaveFieldClick={handleSaveFieldClick}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <OrganizationHubspotNotes orgId={orgId} org={org} />
            </Col>
          </Row>
        </>
      )}
      {/* Add User to Org Modal */}
      <AddUserToTeamOrOrgModal
        isOrg
        teamOrOrg={org}
        show={showAddUserToOrganizationModal}
        orgId={orgId}
        handleClose={() => setShowAddUserToOrganizationModal(false)}
      />

      <EditOrgFieldsModal
        showEditOrgField={showEditOrgField}
        setShowEditOrgField={setShowEditOrgField}
        orgFieldModalParams={orgFieldModalParams}
        handleSaveFieldClick={handleSaveFieldClick}
        isUpdatingOrg={isUpdatingOrg}
        editableOrg={editableOrg}
        handleEditOrgFieldChange={handleEditOrgFieldChange}
      />

      {org && (
        <UpdateOrgSubscriptionIdModal
          show={showSubscriptionIdUpdateModal}
          handleClose={() => setShowSubscriptionIdUpdateModal(false)}
          org={org}
          setShowSubscriptionIdUpdateModal={setShowSubscriptionIdUpdateModal}
        />
      )}
      <OverrideSubscriptionStatusModal
        show={showUpdateSubscriptionStatusOverrideModal}
        handleClose={() => setShowUpdateSubscriptionStatusOverrideModal(false)}
        orgId={orgId}
        organizationSubscription={org?.organization_subscription}
      />

      <HubspotTicketsModal
        show={showHubspotTicketsModal}
        handleClose={() => setShowHubspotTicketsModal(false)}
        orgId={orgId}
        internalAdmins={internalAdmins || []}
      />
    </Container>
  )
}

export default OrganizationDetailPage
